import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { theme } from 'antd';

import { Alerts, EcoSelectStation, InvestigationDashboardParams } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { EPButton } from '@/components';
import ChevronDownIcon from '@/comp/common/icons/ChevronDownIcon';
import { useIsMobile } from '@/hooks/useIsMobile';

import SpecificPowerCard from './SpecificPowerCard';

const { useToken } = theme;

const ChevronIconButton = observer(({ stationStore, token }) => (
  <EPButton
    size="small"
    shape="circle"
    type="primary"
    style={{
      background: token.colorBgElevated,
      borderWidth: 2,
      borderColor: token.colorBgBase,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    icon={
      <ChevronDownIcon
        color={token.colorPrimary}
        style={{ transform: `rotate(${stationStore.isSidebarCollapsed ? '-' : ''}90deg)` }}
      />
    }
    onClick={stationStore.toggleSidebar}
  />
));

const SidePanel = observer(({ tabKey }) => {
  const { isMobile } = useIsMobile();
  const { alertStore, stationStore } = useStores();
  const { token } = useToken();
  const isInvestigationTabActive = tabKey === 'investigation';

  return (
    <Container
      $isCollapsed={stationStore.isSidebarCollapsed}
      onTransitionEnd={() => (stationStore.isSidebarTransitioning = false)}
      $isMobile={isMobile}
    >
      <div className="container">
        <EcoSelectStation />
        {isInvestigationTabActive ? (
          <InvestigationDashboardParams />
        ) : (
          <>
            <SpecificPowerCard />
            <div className="alerts-container">
              <Alerts alerts={alertStore.sortedAlerts} />
            </div>
          </>
        )}
      </div>
      <div className="btn-collapse-container">
        <ChevronIconButton stationStore={stationStore} token={token} />
      </div>
    </Container>
  );
});

const sidePanelDesktopStyling = `
  height: 100%;
  position: relative;
`;
const sidePanelMobileStyling = `
  height: calc(100vh - 46px);
  position: fixed;
  z-index: 3;
  top: 46px;
  left: 0px;
`;

interface ContainerProps {
  $isCollapsed: boolean;
  $isMobile: boolean;
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  ${({ $isMobile }) => ($isMobile ? sidePanelMobileStyling : sidePanelDesktopStyling)}

  flex-shrink: 0;
  flex-grow: 0;
  width: ${({ $isCollapsed }) => ($isCollapsed ? `0px` : '335px')};
  padding: ${({ theme: { token } }) => token.paddingLG}px 0 ${({ theme: { token } }) => token.padding}px 0;
  box-sizing: border-box;
  background-color: ${({ theme: { token } }) => token.colorBgContainer};
  transition: width 0.15s ease-out;
  box-shadow: ${({ theme: { token } }) => token.boxShadowSecondary};

  .container {
    display: ${({ $isCollapsed }) => ($isCollapsed ? 'none' : 'flex')};
    flex-direction: column;
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
  }

  .eco-select-station {
    padding: 0 ${({ theme: { token } }) => token.paddingMD}px;
  }

  .btn-collapse-container {
    position: absolute;
    top: 80px;
    right: 0px;
    transform: translateX(50%);
    z-index: 11;
  }

  .alerts-container {
    height: 100%;
    flex-grow: 1;
    margin-top: ${({ theme: { token } }) => token.marginXXL}px;
  }
`;

export default SidePanel;
